import { CommonModule } from '@angular/common';
import { Component, inject, type OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  type FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { omit } from 'lodash-es';
import { debounceTime, EMPTY, type Observable, switchMap } from 'rxjs';

import type { ControlsOf } from '@core/models/forms.model';
import { NotifyService } from '@core/services/notifyService.service';
import { toBase64 } from '@helpers/file-to-base64';
import { getParamFromRouteOrParent } from '@helpers/get-param-from-route-or-parent';
import { MAX_SMALL_UPLOAD_FILE_SIZE } from '@shared/constants/ume-constants';
import { CkEditorFieldComponent } from '@shared/forms/ck-editor-field/ck-editor-field.component';
import { SanitizeSrcPipe } from '@shared/pipes/sanitize-src.pipe';
import { UmeAlertComponent } from '@widgets/ume-alert/ume-alert.component';

import { ProviderService } from '../../../../../provider/services/provider.service';
import {
  type IServiceOfferingMetadata,
  type ITACInfo,
  SERVICE_ACTIVATION_TYPE,
  type ServiceActivationType,
} from '../../../models/services.model';
import { ServicesManagementService } from '../../../services/services-configuration.service';
import { TermsAndConditionsComponent } from '../terms-and-conditions/terms-and-conditions.component';

const fields = {
  image: 'image',
  activationType: 'activationType',
  name: 'name',
  paidService: 'paidService',
  serviceFee: 'serviceFee',
  description: 'description',
  benefits: 'benefits',
  isHidden: 'isHidden',
} as const;

type FieldType = (typeof fields)[keyof typeof fields];

interface ServiceForm {
  [fields.image]: string | null;
  [fields.activationType]: ServiceActivationType | null;
  [fields.name]: string | null;
  [fields.serviceFee]: number | null;
  [fields.paidService]: boolean | null;
  [fields.description]: string | null;
  [fields.benefits]: string | null;
  [fields.isHidden]: boolean | null;
}

type ServiceFormValue = string | number | boolean | null;

@UntilDestroy()
@Component({
  selector: 'ume-service-configuration-page',
  templateUrl: './service-configuration-page.component.html',
  styleUrls: ['./service-configuration-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInput,
    MatSelectModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatDividerModule,
    UmeAlertComponent,
    SanitizeSrcPipe,
    TermsAndConditionsComponent,
    CkEditorFieldComponent,
    MatProgressSpinner,
  ],
})
export class ServiceConfigurationPageComponent implements OnInit {
  readonly fields = fields;
  readonly activationTypes = SERVICE_ACTIVATION_TYPE;

  private fb: FormBuilder = inject(FormBuilder);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private servicesManagementService: ServicesManagementService = inject(ServicesManagementService);
  private providerService: ProviderService = inject(ProviderService);
  private notify: NotifyService = inject(NotifyService);

  public serviceForm: FormGroup;
  public service!: IServiceOfferingMetadata;
  public canEdit = false;
  public offeringId: string | null = getParamFromRouteOrParent(this.route.snapshot, 'offeringId');
  public terms: ITACInfo | null = null;

  constructor() {
    this.serviceForm = this.fb.group<ControlsOf<ServiceForm>>({
      [fields.image]: new FormControl({ value: null, disabled: false }),
      [fields.activationType]: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      [fields.name]: new FormControl({ value: null, disabled: false }, [Validators.required]),
      [fields.paidService]: new FormControl({ value: null, disabled: false }),
      [fields.serviceFee]: new FormControl({ value: null, disabled: false }),
      [fields.description]: new FormControl({ value: null, disabled: false }),
      [fields.benefits]: new FormControl({ value: null, disabled: false }),
      [fields.isHidden]: new FormControl({ value: null, disabled: false }),
    });
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this)).subscribe((data) => {
      if (data.serviceSetting) {
        this._setServiceAndFormValue(data.serviceSetting);
      }
      this.terms = data.terms ?? null;
    });

    this._checkFieldsChanges();
  }

  public clearFieldValue(field: string): void {
    this.serviceForm.get(field)?.setValue(null, { emitEvent: true });

    if (field === fields.image) {
      this.serviceForm.get(field)?.setValue('', { emitEvent: true });
    }
  }

  public undoChanges(): void {
    if (this.offeringId) {
      this.servicesManagementService
        .undo(this.offeringId)
        .pipe(
          switchMap(() =>
            this.offeringId ? this.servicesManagementService.getService(this.offeringId) : EMPTY
          ),
          untilDestroyed(this)
        )
        .subscribe((service: IServiceOfferingMetadata) => {
          this._setServiceAndFormValue(service);

          this.notify.notifyInfo('Changes have been undone');
        });
    }
  }

  public publishChanges(): void {
    if (this.offeringId) {
      this.servicesManagementService
        .publish(this.offeringId)
        .pipe(
          switchMap(() =>
            this.offeringId ? this.servicesManagementService.getService(this.offeringId) : EMPTY
          ),
          untilDestroyed(this)
        )
        .subscribe((service: IServiceOfferingMetadata) => {
          this.service = service;
          this.notify.notifySuccess('Changes have been published');
        });
    }
  }

  public fileChangeEvent(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    const file = input.files[0];

    if (file.type !== 'image/svg+xml') {
      this.notify.notifyCautionOnly('Type of image must be SVG');
      return;
    }

    if (file) {
      if (file.size > MAX_SMALL_UPLOAD_FILE_SIZE) {
        this.notify.notifyCautionOnly('The maximum allowed file size is 3 MB.');
        input.value = '';
        return;
      }

      this.setImage(file).then();
    }
  }

  public manageServiceFee(paidService: boolean): void {
    if (paidService) {
      this.serviceForm.get(fields.serviceFee)?.patchValue(0, { emitEvent: false });
      this.serviceForm.get(fields.serviceFee)?.enable();
    } else {
      this.serviceForm.get(fields.serviceFee)?.patchValue(null, { emitEvent: false });
      this.serviceForm.get(fields.serviceFee)?.disable();
    }
  }

  public manageActivationTypeField(value: ServiceActivationType): void {
    if (value === SERVICE_ACTIVATION_TYPE.AUTOMATIC) {
      this.serviceForm.get(fields.paidService)?.disable();
    } else if (value === SERVICE_ACTIVATION_TYPE.MANUAL) {
      this.serviceForm.get(fields.paidService)?.enable();
    }
  }

  public refreshInfo(): void {
    if (this.offeringId) {
      this.providerService.getTermsAndConditions(this.offeringId).subscribe((terms: ITACInfo) => {
        this.terms = terms;
      });
    }
  }

  private async setImage(file: File): Promise<void> {
    try {
      const result = await toBase64(file);
      this.serviceForm.get(fields.image)?.setValue(result);
    } catch (_error) {
      this.notify.notifyCautionOnly('Can not convert image to base64 format');
      return;
    }
  }

  private _setServiceAndFormValue(service: IServiceOfferingMetadata): void {
    this.service = service;
    this.canEdit = service.canEdit;
    const fieldsWithoutDraftAndGuid = omit(service, ['isDraft', 'guid', 'visibleTo', 'canEdit']);
    this.serviceForm.setValue(fieldsWithoutDraftAndGuid, { emitEvent: false });
    this.canEdit
      ? this.serviceForm.enable({ emitEvent: false })
      : this.serviceForm.disable({ emitEvent: false });
    this.manageServiceFee(Boolean(fieldsWithoutDraftAndGuid.paidService));
    this.manageActivationTypeField(service.activationType);
  }

  private _checkFieldsChanges(): void {
    Object.values(fields).forEach((field) => {
      this.serviceForm
        .get(field)
        ?.valueChanges.pipe(
          debounceTime(800),
          switchMap((value) => {
            if (field === fields.paidService) {
              this.manageServiceFee(value);
            }

            if (field === fields.activationType) {
              this.manageActivationTypeField(value);
            }

            return this.handleFieldUpdate(field, value);
          }),
          untilDestroyed(this)
        )
        .subscribe((data: IServiceOfferingMetadata) => {
          this.service.isDraft = data.isDraft;
        });
    });
  }

  private handleFieldUpdate(
    field: FieldType,
    value: ServiceFormValue
  ): Observable<IServiceOfferingMetadata | never> {
    if (!this.offeringId) {
      return EMPTY;
    }

    const exceptionFields: FieldType[] = ['image', 'description', 'benefits'];
    const formField = { [field]: value } as Partial<IServiceOfferingMetadata>;

    if (exceptionFields.includes(field) || (value ?? undefined) || value === false) {
      return this.servicesManagementService.updateField(this.offeringId, formField);
    }

    return EMPTY;
  }
}
